/**
 * 인포트렌드 사이징 툴 — 기준 표 (v2.233.0)
 *
 * 전부 엑셀 「Sizing Tool_v2.1.xlsx」(Infortrend · 2016-03 · 회사 NAS 「2. 영업부서/01. 업체별 영업진행 (견적서)」)의
 * 숨은 시트(Disk Performance · M&E Database · Surveillance database · MSRP · InputParam · Calculate)에서 옮긴 값이다.
 *
 * ⚠ 모델 이름·정가(MSRP · USD)는 **2015~2017년 기준**이다. 계산 규칙은 그대로 쓸 수 있지만 제품군·가격은
 *   당시 것이라 「참고」로만 보여 주고 화면에 그 해를 적는다. 새 사이징 툴이 생기면 이 파일만 바꾸면 된다.
 *
 * 디스크 1개 성능 표(219줄)는 disk-perf-table.ts 에 따로 있다(스크립트로 뽑은 것 · 손으로 고치지 말 것).
 */
import type {
  BlockSize, DriveType, IoType, JbodFormFactor, MeCodec, MeFrameRate, MeFrameSize, RaidFormFactor, RandomSeq,
  SvHostBoard, SvQuality, SvResolution, SvStream, VdiDriveType, VdiRaid, VdiWorker,
} from './types';

export { DISK_PERF_TABLE } from './disk-perf-table';

/* ───────────────────────── 공통 ───────────────────────── */

/** 엑셀이 쓰는 상수 — 디스크마다 예약 공간 256MB · 논리 드라이브마다 메타데이터 256MB · 가상화(볼륨 관리) 몫 0.2% */
export const DISK_RESERVED_MB = 256;
export const LD_META_MB = 256;
export const VIRT_RATIO = 0.002;

/**
 * 디스크 표기 용량(TB · 제조사 10진 표기) → 실제 MB. 엑셀 「Capacity」 시트와 같은 식: TB × 1000⁴ ÷ 1024².
 *   예) 4TB → 3,814,697MB. (1TB 표기 디스크가 실제로는 0.909TiB 라는 익숙한 차이가 여기서 난다.)
 */
export function driveTbToRealMb(tb: number): number {
  return (tb * 1000 * 1000 * 1000 * 1000) / 1024 / 1024;
}

/**
 * 영상 제작·CCTV 계산기가 쓰는 디스크 크기별 실제 MB — 엑셀 M&E Database A40:B47 · Surveillance database F24:G31 에
 * **정수로 적힌 값 그대로**(6TB 가 5,722,044 로 적혀 있어 위 식과 2MB 다르다 · 엑셀 결과와 같게 두려고 그대로 둔다).
 */
export const DRIVE_SIZE_MB: Readonly<Record<number, number>> = {
  1: 953674, 2: 1907348, 3: 2861023, 4: 3814697, 5: 4768370, 6: 5722044, 8: 7629394,
};
/** 고를 수 있는 디스크 크기(TB) — 엑셀 목록에 7TB 는 없다(「X」) */
export const DRIVE_SIZES_TB: readonly number[] = [1, 2, 3, 4, 5, 6, 8];

/** 디스크 1개의 「실제 쓸 수 있는」 TB — ((1 − 0.002) × (MB − 256)) ÷ 1024² */
export function usableTbPerDrive(sizeTb: number): number {
  const mb = DRIVE_SIZE_MB[sizeTb];
  if (mb === undefined) throw new Error(`디스크 크기 표에 없는 값: ${sizeTb}TB`);
  return ((1 - VIRT_RATIO) * (mb - DISK_RESERVED_MB)) / (1024 * 1024);
}

/* ───────────────────────── 성능(IOMeter) ───────────────────────── */

export const RANDOM_SEQ_LABEL: Readonly<Record<RandomSeq, string>> = { Random: '랜덤 (Random)', Sequential: '순차 (Sequential)' };
export const IO_LABEL: Readonly<Record<IoType, string>> = {
  RandomRead: '읽기 (Random Read)',
  RandomWrite: '쓰기 (Random Write)',
  OLTP: 'OLTP (읽기·쓰기 섞임)',
  SequentialRead: '읽기 (Sequential Read)',
  SequentialWrite: '쓰기 (Sequential Write)',
};
export const DRIVE_LABEL: Readonly<Record<DriveType, string>> = {
  '15K SAS': 'SAS 15K rpm',
  '10K SAS': 'SAS 10K rpm',
  '7.2K NL-SAS': 'NL-SAS 7.2K rpm',
  'Toshiba SSD': 'SSD (Toshiba)',
};
export const BLOCK_ORDER: readonly BlockSize[] = ['512B', '4K', '8K', '64K', '128K', '512K', '1M'];

/** RAID 인클로저 폼팩터 — 베이 수 (엑셀 Gogo 표) */
export const RAID_FORM_FACTORS: readonly { key: RaidFormFactor; label: string; bay: number }[] = [
  { key: 'RAID2U12', label: '2U · 12베이', bay: 12 },
  { key: 'RAID2U24', label: '2U · 24베이', bay: 24 },
  { key: 'RAID3U16', label: '3U · 16베이', bay: 16 },
  { key: 'RAID4U24', label: '4U · 24베이', bay: 24 },
  { key: 'RAID4U48', label: '4U · 48베이', bay: 48 },
  { key: 'RAID4U60', label: '4U · 60베이', bay: 60 },
];

/**
 * JBOD 폼팩터 — 베이 수 · 권장 단위 · 최대 디스크 수 (엑셀 jbod2 + jbodsuggestnumber5 표).
 *   권장 디스크 수는 **단위의 배수로 올림**한다: 12/24/16베이는 인클로저 단위, 48/60베이는 서랍(16·20개) 단위.
 *   최대 = 12·16·24베이는 14대, 48베이는 6대(288), 60베이는 7대(420) — 표가 거기서 끝나서 그 위는 「고밀도 JBOD 로」 안내.
 */
export const JBOD_FORM_FACTORS: readonly { key: JbodFormFactor; label: string; bay: number; unit: number; maxDrives: number }[] = [
  { key: '2U12', label: '2U · 12베이', bay: 12, unit: 12, maxDrives: 168 },
  { key: '2U24', label: '2U · 24베이', bay: 24, unit: 24, maxDrives: 336 },
  { key: '3U16', label: '3U · 16베이', bay: 16, unit: 16, maxDrives: 224 },
  { key: '4U48', label: '4U · 48베이 (서랍 16개 × 3)', bay: 48, unit: 16, maxDrives: 288 },
  { key: '4U60', label: '4U · 60베이 (서랍 20개 × 3)', bay: 60, unit: 20, maxDrives: 420 },
  { key: 'No need', label: 'JBOD 없음', bay: 0, unit: 0, maxDrives: 0 },
];

/** RAID 폼팩터마다 붙일 수 있는 JBOD (엑셀 RAIDFormfactor 표 AD:AI) */
export const RAID_TO_JBOD: Readonly<Record<RaidFormFactor, readonly JbodFormFactor[]>> = {
  RAID2U12: ['2U12', '4U48', '4U60', 'No need'],
  RAID2U24: ['2U12', '2U24', '3U16', '4U48', '4U60', 'No need'],
  RAID3U16: ['3U16', '4U48', '4U60', 'No need'],
  RAID4U24: ['3U16', '4U48', '4U60', 'No need'],
  RAID4U48: ['4U48', 'No need'],
  RAID4U60: ['4U60', 'No need'],
};

/* ───────────────────────── 영상 제작 (M&E) ───────────────────────── */

export const ME_FRAME_SIZES: readonly MeFrameSize[] = [
  '(5K) 5120 x 2700', '(4K) 4096 x 3112', '(4K) 4096 x 2160', '(4K) 3840 x 2160', '(2K) 2048 x 1556', '(HD) 1280 x 720', 'N/A',
];
export const ME_FRAME_RATES: readonly MeFrameRate[] = ['24fps', '30fps'];
export const ME_CODECS: readonly MeCodec[] = ['10bit RGB (Uncompressed)', 'ProRes (HQ)', 'ProRes 4444'];
export const ME_CODEC_LABEL: Readonly<Record<MeCodec, string>> = {
  '10bit RGB (Uncompressed)': '10bit RGB (무압축)',
  'ProRes (HQ)': 'ProRes (HQ)',
  'ProRes 4444': 'ProRes 4444',
};
export const ME_FRAME_LABEL: Readonly<Record<MeFrameSize, string>> = {
  '(5K) 5120 x 2700': '5K (5120 × 2700)',
  '(4K) 4096 x 3112': '4K (4096 × 3112)',
  '(4K) 4096 x 2160': '4K (4096 × 2160)',
  '(4K) 3840 x 2160': '4K UHD (3840 × 2160)',
  '(2K) 2048 x 1556': '2K (2048 × 1556)',
  '(HD) 1280 x 720': 'HD (1280 × 720)',
  'N/A': '없음',
};

/** 스트림 1개의 처리량(MB/s) — 엑셀 M&E Database A1:C36 (프레임 크기 × 압축 × 24/30fps) */
export const ME_BITRATE: Readonly<Record<MeFrameSize, Readonly<Record<MeCodec, Readonly<Record<MeFrameRate, number>>>>>> = {
  '(5K) 5120 x 2700': { '10bit RGB (Uncompressed)': { '24fps': 1243.75, '30fps': 1554.69 }, 'ProRes (HQ)': { '24fps': 155.47, '30fps': 194.34 }, 'ProRes 4444': { '24fps': 231.34, '30fps': 289.17 } },
  '(4K) 4096 x 3112': { '10bit RGB (Uncompressed)': { '24fps': 1147.5, '30fps': 1434.38 }, 'ProRes (HQ)': { '24fps': 143.44, '30fps': 179.3 }, 'ProRes 4444': { '24fps': 213.44, '30fps': 266.79 } },
  '(4K) 4096 x 2160': { '10bit RGB (Uncompressed)': { '24fps': 849.35, '30fps': 1060 }, 'ProRes (HQ)': { '24fps': 105.58, '30fps': 131.97 }, 'ProRes 4444': { '24fps': 158.37, '30fps': 197.96 } },
  '(4K) 3840 x 2160': { '10bit RGB (Uncompressed)': { '24fps': 796.26, '30fps': 995.33 }, 'ProRes (HQ)': { '24fps': 99.09, '30fps': 123.86 }, 'ProRes 4444': { '24fps': 148.64, '30fps': 185.71 } },
  '(2K) 2048 x 1556': { '10bit RGB (Uncompressed)': { '24fps': 305.92, '30fps': 382.4 }, 'ProRes (HQ)': { '24fps': 38.34, '30fps': 47.92 }, 'ProRes 4444': { '24fps': 57.51, '30fps': 71.88 } },
  '(HD) 1280 x 720': { '10bit RGB (Uncompressed)': { '24fps': 88.47, '30fps': 110.59 }, 'ProRes (HQ)': { '24fps': 12.39, '30fps': 15.48 }, 'ProRes 4444': { '24fps': 18.58, '30fps': 23.22 } },
  'N/A': { '10bit RGB (Uncompressed)': { '24fps': 0, '30fps': 0 }, 'ProRes (HQ)': { '24fps': 0, '30fps': 0 }, 'ProRes 4444': { '24fps': 0, '30fps': 0 } },
};

export type MeFamily = 'DS3000' | 'DS4000';

/**
 * 제품군별 처리량 — 엑셀 M&E Database T22:X39.
 *   perDrive: 디스크 1개가 내는 MB/s [DS3000 읽기, DS4000 읽기, DS3000 쓰기, DS4000 쓰기]
 *     (엑셀은 「최대 처리량 ÷ 데이터 디스크 수」로 적어 두었다 — DS3000 은 ÷52, DS4000 은 ÷24 · 쓰기 DS3000 은 상수)
 *   max: 그 프레임 크기에서 제품군이 내는 최대 MB/s [같은 순서]. 1 이면 「그 제품군으로는 안 된다」는 뜻(5K · 4K 3112 의 DS3000).
 */
export const ME_PERF: Readonly<Record<MeFrameSize, { perDrive: readonly [number, number, number, number]; max: readonly [number, number, number, number] }>> = {
  '(5K) 5120 x 2700': { perDrive: [1, 130.25, 1, 115.13], max: [1, 7729, 1, 4467] },
  '(4K) 4096 x 3112': { perDrive: [1, 130.25, 1, 115.13], max: [1, 7753, 1, 4516] },
  '(4K) 4096 x 2160': { perDrive: [5100 / 52, 3126 / 24, 85, 2763 / 24], max: [5100, 7569, 1877, 4378] },
  '(4K) 3840 x 2160': { perDrive: [5038 / 52, 2938 / 24, 83, 2653 / 24], max: [5038, 7529, 1792, 4338] },
  '(2K) 2048 x 1556': { perDrive: [4864 / 52, 2875 / 24, 85, 2675 / 24], max: [4864, 7040, 1639, 3717] },
  '(HD) 1280 x 720': { perDrive: [3850 / 52, 3019 / 24, 50, 1478 / 24], max: [3850, 5197, 1175, 2494] },
  'N/A': { perDrive: [0, 0, 0, 0], max: [0, 0, 0, 0] },
};

/** M&E 시트 오른쪽 참고 표 — 모델별 동시 스트림 수(읽기/쓰기)와 최대 처리량(MB/s · 100% 순차 기준) */
export const ME_STREAM_REF: readonly { frame: string; ds4000: readonly [number, number]; ds3000: readonly [number, number] | null }[] = [
  { frame: '5K 5120×2700 10bit RGB 24fps', ds4000: [6, 3], ds3000: null },
  { frame: '4K 4096×3112 10bit RGB 24fps', ds4000: [6, 3], ds3000: null },
  { frame: '4K 4096×2160 10bit RGB 24fps', ds4000: [8, 5], ds3000: [5, 2] },
  { frame: '4K 3840×2160 10bit RGB 24fps', ds4000: [9, 5], ds3000: [6, 2] },
  { frame: '2K 2048×1556 10bit RGB 24fps', ds4000: [23, 12], ds3000: [15, 5] },
  { frame: 'HD 1280×720 10bit 24fps', ds4000: [68, 27], ds3000: [43, 13] },
];
export const ME_THROUGHPUT_REF: readonly { frame: string; ds4000: readonly [number, number]; ds3000: readonly [number, number] | null }[] = [
  { frame: '5K 5120×2700 10bit RGB', ds4000: [7729, 4467], ds3000: null },
  { frame: '4K 4096×3112 10bit RGB', ds4000: [7753, 4516], ds3000: null },
  { frame: '4K 4096×2160 10bit RGB', ds4000: [7569, 4378], ds3000: [5100, 1877] },
  { frame: '4K 3840×2160 10bit RGB', ds4000: [7529, 4338], ds3000: [5038, 1792] },
  { frame: '2K 2048×1556 10bit RGB', ds4000: [7040, 3717], ds3000: [4864, 1639] },
  { frame: 'HD 1280×720 10bit RGB', ds4000: [6078, 2494], ds3000: [3850, 1175] },
];

/** 정가(MSRP · USD · 2015~2017 당시) — 엑셀 MSRP 시트 */
export const MSRP: Readonly<Record<string, number>> = {
  'EonStor DS 4016RU': 14000, 'EonStor DS 3012RT': 9000, 'EonStor DS 3016RT': 11000, 'EonStor DS 3024RT': 13800,
  'EonStor DS 3048RTE': 23200, 'EonStor DS 3060RTE': 25400,
  'EonStor DS 4016R': 13200, 'EonStor DS 3012R': 8060, 'EonStor DS 3016R': 9700, 'EonStor DS 3024R': 12540,
  'EonStor DS 3048RE': 21320, 'EonStor DS 3060RE': 23760,
  'EonStor DS 1012G': 2970, 'EonStor DS 1012RC': 5508, 'EonStor DS 1016G': 3564, 'EonStor DS 1016RC': 6696,
  'JB 3016R': 5220, 'JB 2012G': 2880, 'JB 2012R': 3510, 'JB 2016G': 3420, 'JB 2016R': 4230,
  'JB 2048G': 12141, 'JB 2048R': 14724, 'JB 2060G': 13356, 'JB 2060R': 16196,
  'FC 16Gb Host board': 1260, 'iSCSI 1Gb Host board': 144, 'iSCSI 10Gb(RJ45) Host board': 864,
};
/** M&E 정가에 더하는 것 — 메모리 16GB 로 올리는 차액(DS3000 560 · DS4000U 864) · FC 16Gb 호스트 보드(DS3000 2장 · DS4000 4장) */
export const ME_MEM_UPGRADE: Readonly<Record<'ds3000' | 'ds4000std' | 'ds4000highIops', number>> = { ds3000: 560, ds4000std: 560, ds4000highIops: 864 };
export const ME_HOST_BOARDS: Readonly<Record<MeFamily, number>> = { DS3000: 2, DS4000: 4 };

export interface MeSystem {
  name: string;
  family: MeFamily;
  bay: number;
  /** 서랍식(48·60베이)이면 서랍 하나의 베이 수, 아니면 bay 와 같다 */
  unit: number;
  drawers: boolean;
  heightU: number;
  /** 표준 모델 / High IOPS(T·U) 모델 정가 */
  msrpStd: number;
  msrpHighIops: number;
}
export interface MeJbod {
  name: string;
  bay: number;
  unit: number;
  drawers: boolean;
  heightU: number;
  /** null = 당시 정가 미정 (JB 3060R 「N/A(Q1, 2016)」) */
  msrp: number | null;
  /** 체인으로 이을 수 있는 최대 대수(12·16베이) 또는 최대 서랍 수(48·60베이) */
  maxUnits: number;
}
const ME_SYS: Readonly<Record<string, MeSystem>> = {
  ds4016: { name: 'EonStor DS 4016R Gen2', family: 'DS4000', bay: 16, unit: 16, drawers: false, heightU: 3, msrpStd: 13200, msrpHighIops: 14000 },
  ds3012: { name: 'EonStor DS 3012R', family: 'DS3000', bay: 12, unit: 12, drawers: false, heightU: 2, msrpStd: 8060, msrpHighIops: 9000 },
  ds3016: { name: 'EonStor DS 3016R', family: 'DS3000', bay: 16, unit: 16, drawers: false, heightU: 3, msrpStd: 9700, msrpHighIops: 11000 },
  ds3024: { name: 'EonStor DS 3024R', family: 'DS3000', bay: 24, unit: 24, drawers: false, heightU: 4, msrpStd: 12540, msrpHighIops: 13800 },
  ds3048: { name: 'EonStor DS 3048RE', family: 'DS3000', bay: 48, unit: 16, drawers: true, heightU: 4, msrpStd: 21320, msrpHighIops: 23200 },
  ds3060: { name: 'EonStor DS 3060RE', family: 'DS3000', bay: 60, unit: 20, drawers: true, heightU: 4, msrpStd: 23760, msrpHighIops: 25400 },
};
const ME_JB: Readonly<Record<string, MeJbod>> = {
  jb3016: { name: 'JB 3016R', bay: 16, unit: 16, drawers: false, heightU: 3, msrp: 5220, maxUnits: 14 },
  jb3060: { name: 'JB 3060R', bay: 60, unit: 20, drawers: true, heightU: 4, msrp: null, maxUnits: 21 },
  jb2012: { name: 'JB 2012R', bay: 12, unit: 12, drawers: false, heightU: 2, msrp: 3510, maxUnits: 14 },
  jb2016: { name: 'JB 2016R', bay: 16, unit: 16, drawers: false, heightU: 3, msrp: 4230, maxUnits: 14 },
  jb2048: { name: 'JB 2048R', bay: 48, unit: 16, drawers: true, heightU: 4, msrp: 14724, maxUnits: 18 },
  jb2060: { name: 'JB 2060R', bay: 60, unit: 20, drawers: true, heightU: 4, msrp: 16196, maxUnits: 15 },
};
/** 추천 표의 줄 — 엑셀 M&E 시트 B24:C37 순서 그대로 */
export const ME_CONFIGS: readonly { system: MeSystem; jbod: MeJbod }[] = [
  { system: ME_SYS.ds4016!, jbod: ME_JB.jb3016! },
  { system: ME_SYS.ds4016!, jbod: ME_JB.jb3060! },
  { system: ME_SYS.ds3012!, jbod: ME_JB.jb2012! },
  { system: ME_SYS.ds3012!, jbod: ME_JB.jb2048! },
  { system: ME_SYS.ds3012!, jbod: ME_JB.jb2060! },
  { system: ME_SYS.ds3016!, jbod: ME_JB.jb2016! },
  { system: ME_SYS.ds3016!, jbod: ME_JB.jb2048! },
  { system: ME_SYS.ds3016!, jbod: ME_JB.jb2060! },
  { system: ME_SYS.ds3024!, jbod: ME_JB.jb2016! },
  { system: ME_SYS.ds3024!, jbod: ME_JB.jb2048! },
  { system: ME_SYS.ds3024!, jbod: ME_JB.jb2060! },
  { system: ME_SYS.ds3048!, jbod: ME_JB.jb2048! },
  { system: ME_SYS.ds3060!, jbod: ME_JB.jb2060! },
];

/* ───────────────────────── CCTV (감시) ───────────────────────── */

export const SV_STREAMS: readonly SvStream[] = ['N/A', 'MJPEG', 'H.264'];
export const SV_STREAM_LABEL: Readonly<Record<SvStream, string>> = { 'N/A': '없음 (쓰지 않음)', MJPEG: 'MJPEG', 'H.264': 'H.264' };
export const SV_RESOLUTIONS: readonly SvResolution[] = ['D1 (704x480)', '720P HD', '1080P HD', '1.3 Megapixel', '3 Megapixel', '5 Megapixel', '10 Megapixel'];
export const SV_RESOLUTION_LABEL: Readonly<Record<SvResolution, string>> = {
  'D1 (704x480)': 'D1 (704 × 480)', '720P HD': '720P HD', '1080P HD': '1080P HD',
  '1.3 Megapixel': '1.3 메가픽셀', '3 Megapixel': '3 메가픽셀', '5 Megapixel': '5 메가픽셀', '10 Megapixel': '10 메가픽셀',
};
export const SV_QUALITIES: readonly SvQuality[] = ['Low', 'Normal', 'High'];
export const SV_QUALITY_LABEL: Readonly<Record<SvQuality, string>> = { Low: '낮음', Normal: '보통', High: '높음' };
/** 카메라 1대 · 1fps 당 Mbps — 엑셀 Surveillance database A3:D30 [낮음, 보통, 높음] */
export const SV_BITRATE: Readonly<Record<SvResolution, Readonly<Record<'MJPEG' | 'H.264', readonly [number, number, number]>>>> = {
  'D1 (704x480)': { MJPEG: [0.12, 0.24, 0.48], 'H.264': [0.024, 0.048, 0.096] },
  '720P HD': { MJPEG: [0.32, 0.64, 0.96], 'H.264': [0.064, 0.128, 0.192] },
  '1080P HD': { MJPEG: [0.8, 1.4, 2], 'H.264': [0.16, 0.272, 0.4] },
  '1.3 Megapixel': { MJPEG: [0.56, 0.96, 1.4], 'H.264': [0.112, 0.192, 0.288] },
  '3 Megapixel': { MJPEG: [1, 1.7, 2.6], 'H.264': [0.2, 0.336, 0.512] },
  '5 Megapixel': { MJPEG: [1.2, 2, 3.2], 'H.264': [0.24, 0.4, 0.64] },
  '10 Megapixel': { MJPEG: [2.4, 4, 7.2], 'H.264': [0.48, 0.8, 1.4] },
};
export const SV_FPS: readonly number[] = [1, 2, 3, 5, 7, 10, 12, 15, 20, 25, 30];
/** 엑셀 각주 「Max FPS: 10MP=6, 5MP=10, 3MP=12, 1080P=25, 1.3MP and less=30」 — 넘으면 경고만 */
export const SV_MAX_FPS: Readonly<Record<SvResolution, number>> = {
  'D1 (704x480)': 30, '720P HD': 30, '1080P HD': 25, '1.3 Megapixel': 30, '3 Megapixel': 12, '5 Megapixel': 10, '10 Megapixel': 6,
};
export const SV_HOURS: readonly number[] = Array.from({ length: 24 }, (_, i) => i + 1);
/**
 * 컨트롤러 판정 기준(MB/s · 랜덤 1M · 온보드 1GbE 기준) — 엑셀 Surveillance database B36:B37.
 *   총 대역폭이 352 까지면 단일 컨트롤러(G) · 668 미만이면 이중 컨트롤러(R) · 그 위는 「이 제품군으로는 안 됨」.
 */
export const SV_CONTROLLER_LIMIT = { single: 352, dual: 668 } as const;
export const SV_HOST_BOARDS: readonly { key: SvHostBoard; label: string; price: number }[] = [
  { key: 'none', label: '없음 — 온보드 1GbE iSCSI 4포트(컨트롤러당)', price: 0 },
  { key: '1g', label: '1GbE iSCSI 보드 추가 — 컨트롤러당 1GbE 8포트', price: 144 },
  { key: '10g', label: '10GbE iSCSI(RJ-45) 보드 추가 — 1GbE 4포트 + 10GbE 2포트', price: 864 },
];
export interface SvSystem { name: string; bay: number; heightU: number; priceSingle: number; priceDual: number }
export interface SvJbod { name: string; bay: number; unit: number; drawers: boolean; heightU: number; priceSingle: number; priceDual: number; maxJbods: number; maxDrawers: number }
const SV_SYS: Readonly<Record<string, SvSystem>> = {
  ds1012: { name: 'EonStor DS 1012', bay: 12, heightU: 2, priceSingle: 2970, priceDual: 5508 },
  ds1016: { name: 'EonStor DS 1016', bay: 16, heightU: 3, priceSingle: 3564, priceDual: 6696 },
};
const SV_JB: Readonly<Record<string, SvJbod>> = {
  jb2012: { name: 'JB 2012', bay: 12, unit: 12, drawers: false, heightU: 2, priceSingle: 2880, priceDual: 3510, maxJbods: 14, maxDrawers: 0 },
  jb2016: { name: 'JB 2016', bay: 16, unit: 16, drawers: false, heightU: 3, priceSingle: 3420, priceDual: 4230, maxJbods: 14, maxDrawers: 0 },
  jb2048: { name: 'JB 2048', bay: 48, unit: 16, drawers: true, heightU: 4, priceSingle: 12141, priceDual: 14724, maxJbods: 6, maxDrawers: 18 },
  jb2060: { name: 'JB 2060', bay: 60, unit: 20, drawers: true, heightU: 4, priceSingle: 13356, priceDual: 16196, maxJbods: 5, maxDrawers: 15 },
};
/** 추천 표의 줄 — 엑셀 Surveillance 시트 G5:I10 순서(12베이 + 12/48/60 · 16베이 + 16/48/60) */
export const SV_CONFIGS: readonly { system: SvSystem; jbod: SvJbod }[] = [
  { system: SV_SYS.ds1012!, jbod: SV_JB.jb2012! },
  { system: SV_SYS.ds1012!, jbod: SV_JB.jb2048! },
  { system: SV_SYS.ds1012!, jbod: SV_JB.jb2060! },
  { system: SV_SYS.ds1016!, jbod: SV_JB.jb2016! },
  { system: SV_SYS.ds1016!, jbod: SV_JB.jb2048! },
  { system: SV_SYS.ds1016!, jbod: SV_JB.jb2060! },
];

/* ───────────────────────── VDI ───────────────────────── */

export const VDI_RAIDS: readonly VdiRaid[] = ['RAID 10', 'RAID 5', 'RAID 6'];
export const VDI_DRIVE_TYPES: readonly VdiDriveType[] = ['SAS15K', 'SAS10K', 'NLSAS7.2K', 'SSD'];
export const VDI_DRIVE_LABEL: Readonly<Record<VdiDriveType, string>> = {
  SAS15K: 'SAS 15K rpm', SAS10K: 'SAS 10K rpm', 'NLSAS7.2K': 'NL-SAS 7.2K rpm', SSD: 'SSD',
};
/** 디스크 종류별 고를 수 있는 용량(GB) — 엑셀 InputParam */
export const VDI_DRIVE_CAPACITIES: Readonly<Record<VdiDriveType, readonly number[]>> = {
  SAS15K: [600, 450, 300], SAS10K: [1200, 900, 600, 450, 300], 'NLSAS7.2K': [2000, 1000], SSD: [800, 400, 200],
};
/** 디스크 1개가 내는 IOPS — 엑셀 Calculate F2:I6 (RAID 6 / RAID 5 / RAID 10) */
export const VDI_IOPS_PER_DISK: Readonly<Record<VdiDriveType, Readonly<Record<VdiRaid, number>>>> = {
  SAS15K: { 'RAID 6': 100, 'RAID 5': 138, 'RAID 10': 250 },
  SAS10K: { 'RAID 6': 84, 'RAID 5': 118, 'RAID 10': 213 },
  'NLSAS7.2K': { 'RAID 6': 43, 'RAID 5': 60, 'RAID 10': 106 },
  SSD: { 'RAID 6': 9200, 'RAID 5': 11000, 'RAID 10': 17000 },
};
export const VDI_WORKERS: readonly { key: VdiWorker; label: string; iops: number }[] = [
  { key: 'light', label: '가벼운 작업자 (앱 1~5개)', iops: 10 },
  { key: 'knowledge', label: '지식 근로자 (앱 1~5개)', iops: 20 },
  { key: 'heavy', label: '무거운 작업자 (앱 5개 이상)', iops: 50 },
];
/** 모델별 최대 IOPS · 최대 디스크 수 — 엑셀 Calculate H51:K55 */
export const VDI_MODELS: readonly { name: string; maxIops: number; maxDrives: number; spec: string }[] = [
  { name: 'EonStor DS 4000U', maxIops: 231000, maxDrives: 444, spec: 'LFF/SFF 436/444' },
  { name: 'EonStor DS 3000T', maxIops: 167000, maxDrives: 360, spec: 'LFF/SFF 360/360' },
  { name: 'EonStor DS 2000', maxIops: 75000, maxDrives: 360, spec: 'LFF/SFF 360/360' },
  { name: 'EonStor DS 1000T', maxIops: 47000, maxDrives: 360, spec: 'LFF/SFF 360/360' },
];
/** 부트 스톰용 SSD 논리 드라이브 — 디스크 1개 IOPS(RAID 1 17000 · RAID 5 11000 · RAID 6 9200) · 최소 개수 */
export const VDI_BOOT_SSD = { raid1: { iops: 17000, min: 2 }, raid5: { iops: 11000, min: 3 }, raid6: { iops: 9200, min: 4 } } as const;
